/**
 * SettingsKioskSection — persisted kiosk settings: scene-rotation interval,
 * which scenes are enabled, and the "Kiosk auto-launch on Pi boot" toggle.
 * Reads/writes the `kiosk` namespace via the agent's user-data store.
 */
import { useEffect, useState } from 'react';

import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { userDataApi } from '../../lib/userdata-api';
import { DEFAULT_SCENE_IDS, type SceneId } from '../kiosk/sceneRegistry';

// ── Settings shape ────────────────────────────────────────────────────────────

interface KioskSettings {
  sceneIntervalSec: number;
  autoLaunch: boolean;
  scenes: SceneId[];
}

const DEFAULTS: KioskSettings = {
  sceneIntervalSec: 20,
  autoLaunch: false,
  scenes: [...DEFAULT_SCENE_IDS],
};

function narrowKioskSettings(raw: unknown): KioskSettings {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DEFAULTS };
  }
  const obj = raw as Record<string, unknown>;

  const sceneIntervalSec =
    typeof obj.sceneIntervalSec === 'number' && obj.sceneIntervalSec > 0
      ? obj.sceneIntervalSec
      : DEFAULTS.sceneIntervalSec;

  const autoLaunch =
    typeof obj.autoLaunch === 'boolean' ? obj.autoLaunch : DEFAULTS.autoLaunch;

  // Legacy persisted settings may still list removed scenes (e.g. the
  // old 'tank-vision' scene) — they are skipped, never deleted upstream.
  const scenes: SceneId[] = Array.isArray(obj.scenes)
    ? (obj.scenes as string[]).filter(
        (s): s is SceneId => s === 'big-numbers' || s === 'schedule',
      )
    : [...DEFAULTS.scenes];

  return {
    sceneIntervalSec,
    autoLaunch,
    scenes: scenes.length > 0 ? scenes : [...DEFAULTS.scenes],
  };
}

// ── Scene labels ──────────────────────────────────────────────────────────────

const SCENE_META: Record<SceneId, string> = {
  'big-numbers': 'Big Numbers',
  schedule: "Today's Schedule",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function SettingsKioskSection() {
  const [settings, setSettings] = useState<KioskSettings>({ ...DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load persisted settings on mount.
  useEffect(() => {
    void (async () => {
      const result = await userDataApi.get('kiosk');
      if (result.ok) {
        setSettings(narrowKioskSettings(result.value.data));
      }
    })();
  }, []);

  async function save(next: KioskSettings) {
    setSettings(next);
    setSaveError(null);
    setSaving(true);
    const result = await userDataApi.put('kiosk', next satisfies KioskSettings);
    setSaving(false);
    if (!result.ok) setSaveError(result.error);
  }

  function handleIntervalChange(raw: string) {
    const v = parseInt(raw, 10);
    if (!Number.isFinite(v) || v < 5) return;
    void save({ ...settings, sceneIntervalSec: v });
  }

  function handleAutoLaunchToggle() {
    void save({ ...settings, autoLaunch: !settings.autoLaunch });
  }

  function handleSceneToggle(id: SceneId) {
    const already = settings.scenes.includes(id);
    // Keep at least one scene enabled.
    if (already && settings.scenes.length <= 1) return;
    const next: SceneId[] = already
      ? settings.scenes.filter((s) => s !== id)
      : [...settings.scenes, id];
    void save({ ...settings, scenes: next });
  }

  return (
    <Card className="mt-6" data-testid="settings-kiosk-section">
      <CardHeader>
        <CardTitle>Kiosk Display</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-xs text-slate-400 mb-6">
          Configure the fullscreen kiosk mode available at{' '}
          <code className="text-slate-300">/kiosk</code>. Scenes rotate
          automatically on the interval below.
        </p>

        {/* Scene interval */}
        <div className="mb-6">
          <label
            htmlFor="kiosk-interval"
            className="block text-sm font-medium text-slate-200 mb-1"
          >
            Scene rotation interval
            {saving ? (
              <span className="ml-2 text-xs text-slate-500">Saving…</span>
            ) : null}
          </label>
          <div className="flex items-center gap-2">
            <input
              id="kiosk-interval"
              type="number"
              min={5}
              max={300}
              step={5}
              value={settings.sceneIntervalSec}
              onChange={(e) => handleIntervalChange(e.target.value)}
              className="w-24 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-slate-200 tabular-nums"
              data-testid="kiosk-interval-input"
            />
            <span className="text-sm text-slate-400">seconds</span>
          </div>
        </div>

        {/* Scene visibility toggles */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-200 mb-2">
            Active scenes
          </p>
          <ul className="space-y-2">
            {(Object.keys(SCENE_META) as SceneId[]).map((id) => {
              const enabled = settings.scenes.includes(id);
              return (
                <li key={id} className="flex items-center gap-3">
                  <input
                    id={`kiosk-scene-${id}`}
                    type="checkbox"
                    checked={enabled}
                    onChange={() => handleSceneToggle(id)}
                    className="accent-[color:var(--accent)] w-4 h-4"
                    data-testid={`kiosk-scene-toggle-${id}`}
                  />
                  <label
                    htmlFor={`kiosk-scene-${id}`}
                    className="text-sm text-slate-300 cursor-pointer"
                  >
                    {SCENE_META[id]}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Auto-launch toggle */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/60">
          <input
            id="kiosk-autolaunch"
            type="checkbox"
            checked={settings.autoLaunch}
            onChange={handleAutoLaunchToggle}
            className="accent-[color:var(--accent)] w-4 h-4 mt-0.5 flex-none"
            data-testid="kiosk-autolaunch-toggle"
          />
          <div>
            <label
              htmlFor="kiosk-autolaunch"
              className="text-sm font-medium text-slate-200 cursor-pointer"
            >
              Kiosk auto-launch on Pi boot
            </label>
            <p className="text-xs text-slate-500 mt-0.5">
              When enabled, the Pi will open the browser to{' '}
              <code className="text-slate-400">/kiosk</code> on startup.
              Configure the Pi-side boot hook separately per the Pi Kiosk
              Display setup guide.
            </p>
          </div>
        </div>

        {saveError !== null ? (
          <p
            className="mt-3 text-xs text-[color:var(--bad)]"
            role="alert"
            data-testid="kiosk-save-error"
          >
            Could not save: {saveError}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
